'use client';

import { FAQ } from '@/components/FAQ';

export interface HVACRepairFAQsProps {
	testId?: string;
}

const HVACRepairFAQs = (props: HVACRepairFAQsProps) => {
	const { testId = 'HVACRepairFAQs' } = props;

	return (
		<FAQ includeStructuredData testId={testId} className="mb-10 lg:mb-20">
			<FAQ.Title>FAQ</FAQ.Title>
			<FAQ.Block>
				<FAQ.Item>
					<FAQ.Question panelId="1">
						What HVAC repair services do you offer?
					</FAQ.Question>
					<FAQ.Answer panelId="1">
						<p>
							We provide a full range of HVAC repair services, including air
							conditioning repair, furnace repair, heat pump repair, and duct
							repair. Our expert HVAC technicians handle everything from small
							fixes to major system repairs.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="2">
						What are some signs you need HVAC repair?
					</FAQ.Question>
					<FAQ.Answer panelId="2">
						<p>
							Common signs you need HVAC repair include uneven heating or
							cooling, strange noises, frequent cycling, complete breakdowns, or
							a sudden increase in your energy bills. If you’re unsure, it’s
							always best to have a professional HVAC repairman inspect your
							system.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="3">
						How much does HVAC repair cost?
					</FAQ.Question>
					<FAQ.Answer panelId="3">
						<p>
							HVAC repair costs vary depending on the issue. Simple HVAC system
							repairs like a thermostat adjustment may be inexpensive, while
							larger repairs like motor or compressor replacements may cost
							more. Our technicians will provide an upfront quote after they
							diagnose your system.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="4">
						What should I expect during an HVAC repair visit?
					</FAQ.Question>
					<FAQ.Answer panelId="4">
						<p>
							Our technician will inspect your system, diagnose the issue, and
							provide a detailed quote. Once approved, they will carry out the
							necessary repairs and ensure your system is back to optimal
							performance.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="5">
						Can I schedule emergency HVAC repairs?
					</FAQ.Question>
					<FAQ.Answer panelId="5">
						<p>
							If your system breaks down unexpectedly, we’ll connect you with
							one of our trusted local HVAC companies — they’ll be best equipped
							to handle your repair quickly and safely. While we can’t guarantee
							24-hour service in every area, we’ll do our best to match you with
							someone who can help as soon as possible.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="6">
						How long does HVAC system repair take?
					</FAQ.Question>
					<FAQ.Answer panelId="6">
						<p>
							Most HVAC repairs can be completed within a few hours. However,
							more complex repairs may take longer depending on the nature of
							the issue and parts availability.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="7">
						Is HVAC repair covered by warranty?
					</FAQ.Question>
					<FAQ.Answer panelId="7">
						<p>
							Our team will review your system’s warranty details and help you
							determine whether repair or replacement is the best choice for
							your situation. Here’s what our Technical Advisors generally
							recommend:
						</p>
						<ul className="ml-10 list-outside list-disc">
							<li>
								<strong>For systems under 5 years old:</strong> Repairs are
								usually recommended, especially if the warranty covers
								significant repair costs.
							</li>
							<li>
								<strong>For systems between 5 and 10 years old:</strong> Parts
								might still be covered under a manufacturer’s warranty,
								particularly if the system was registered at installation.
							</li>
							<li>
								<strong>
									For systems over 10 years old with repair costs under $3,000:
								</strong>
								Repairs may be the best option, especially for one-time fixes
								like replacing a blower motor or coil.
							</li>
							<li>
								<strong>For systems with repair costs over $3,000:</strong>{' '}
								Replacement might be more cost-effective, but consider the
								specific repair and system performance.
							</li>
							<li>
								<strong>For systems over 15 years old:</strong> Consider
								replacing the system as it approaches the end of its lifespan;
								however, heating or air conditioner repair may still be an
								option depending on usage.
							</li>
						</ul>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="8">
						Do you offer affordable local HVAC repair options?
					</FAQ.Question>
					<FAQ.Answer panelId="8">
						<p>
							Yes, we offer affordable HVAC unit repair services and provide
							upfront pricing. Flexible financing options are also available,
							subject to availability and homeowner qualification.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="9">
						Can HVAC repairs improve my system’s efficiency?
					</FAQ.Question>
					<FAQ.Answer panelId="9">
						<p>
							Yes, addressing issues like dirty filters, malfunctioning parts,
							or refrigerant leaks can help your system run more efficiently,
							reducing energy consumption and saving you money on utility bills.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="10">
						What areas do you serve for HVAC repairs?
					</FAQ.Question>
					<FAQ.Answer panelId="10">
						<p>
							We offer residential furnace and AC repair services in most areas
							of the U.S.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="11">
						What should I do if my HVAC system stops working?
					</FAQ.Question>
					<FAQ.Answer panelId="11">
						<p>
							If your HVAC system stops working, check your thermostat, air
							filter, and circuit breaker. If the issue persists, contact us for
							an emergency repair service to get your system running again
							quickly.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
				<FAQ.Item>
					<FAQ.Question panelId="12">
						How often should I get HVAC maintenance to prevent repairs?
					</FAQ.Question>
					<FAQ.Answer panelId="12">
						<p>
							Regular HVAC maintenance should be performed twice a year, ideally
							before the heating and cooling seasons. Preventative HVAC service
							can help avoid unexpected breakdowns and extend the life of your
							system.
						</p>
					</FAQ.Answer>
				</FAQ.Item>
			</FAQ.Block>
		</FAQ>
	);
};

export default HVACRepairFAQs;
